import $ from 'jquery';
import EventEmitter from 'events';
import SyosApiDataService from './api/SyosApiDataService';
import SyosScreenContainer from './SyosScreenContainer';
import SyosSeatContainer from './SyosSeatContainer';
import SyosPriceTypeSelector from './SyosPriceTypeSelector';
import SyosSeatInfoPopover from './SyosSeatInfoPopover';
import SyosLegend from './SyosLegend';
import SyosCartContainer from './SyosCartContainer';
import SyosConfig, { defaultSyosConfig, StateClass, SyosSelectionMode } from './SyosConfig';
import SyosMobileCartControls from './SyosMobileCartControls';
import SyosBusyIndicator from './SyosBusyIndicator';
import SyosAjaxErrorModal from './modals/SyosAjaxErrorModal';
import SyosEvents from './utils/SyosEvents';
import { toggleBodyClass } from './utils/html';
import { loadModal } from '../AsyncLoader';
import SyosPageComponent from './components/SyosPageComponent';
import { getCurrencyFormatterSync } from '../tnew/currency/getCurrencyFormatter';
import SyosAnnounce from './SyosAnnounce';
import SyosMapControlsList from './SyosMapControlsList';
import ViewportZoomControl from './utils/ViewportZoomControl';
import SyosScreenHeader from './SyosScreenHeader';
import IconRenderer from './seatIcons/IconRenderer';
import { Screen } from './api/types';
import SyosMediaQuery from './utils/SyosMediaQuery';
import FlexPriceTypeSelectionHelper from './priceTypeSelectionHelpers/FlexPriceTypeSelectionHelper';
import StandardPriceTypeSelectionHelper from './priceTypeSelectionHelpers/StandardPriceTypeSelectionHelper';
import { IPriceTypeSelectionHelper } from './priceTypeSelectionHelpers/types';
import type SyosSeat from './SyosSeat';
import { ISyosLabels } from './labels/types';
import buildStandardSyosLabels from './labels/buildStandardSyosLabels';
import SyosMobileFocusTrapper from './utils/SyosMobileFocusTrapper';

export interface ISyosSeatReservationResult {
  isSuccessful: boolean;
  errorTitle?: string;
  errorMessage?: string,
  onSuccess?: () => void;
}

function getHtml(mode: SyosSelectionMode) {
  return `
    ${mode === 'flex' ? ' <div class="tn-flex-syos-instructions"></div> ' : ''}

    <div class="tn-syos-seat-info-popover" aria-hidden="true"></div>

    <div class="tn-syos__layout-container">
      <div class="tn-syos-busy-indicator"></div>

      <div class="tn-syos-screen-container"></div>

      <div class="tn-syos__screen-view-components">
        <div class="tn-syos-screen-header"></div>
        <div class="tn-syos-seat-container__wrapper">
          <div class="tn-syos-seat-container">
            <section id="tn-syos-legend-container" class="tn-syos-legend__container"></section>
          </div>
        </div>
        <div class="tn-syos-map-controls-list__container"></div>
      </div>

      <div class="tn-syos-mobile-cart-controls tn-focus-outline" tabindex="-1"></div>
    </div>

    ${mode === 'flex' ? '<div class="tn-flex-syos__ticketing-mode-switch"></div>' : ''}
  `;
}

class SyosController extends EventEmitter {
  currentScreen?: Screen;

  private maximumSeatCount = 0;

  $el?: JQuery<Element>;

  announce?: SyosAnnounce;

  dataService: SyosApiDataService;

  legend?: SyosLegend;

  cartContainer?: SyosCartContainer;

  priceTypeSelector?: SyosPriceTypeSelector;

  seatInfoPopover?: SyosSeatInfoPopover;

  busyIndicator?: SyosBusyIndicator;

  mobileCartControls?: SyosMobileCartControls;

  screenContainer?: SyosScreenContainer;

  mapControlsList?: SyosMapControlsList;

  seatContainer?: SyosSeatContainer;

  screenHeader?: SyosScreenHeader;

  mobileFocusTrapper?: SyosMobileFocusTrapper;
  
  zoomControl?: ViewportZoomControl;

  config: SyosConfig;

  constructor(performanceId: number, private screenId: number | undefined, configOverrides: Partial<SyosConfig>, container: Element, providedLabels?: ISyosLabels, packageId: number = 0) {
    const config: SyosConfig = {
      ...defaultSyosConfig,
      ...configOverrides,
    };

    super();

    this.config = config;

    this.setMaxListeners(13);

    this.$el = $(container);
    this.$el.html(getHtml(config.selectionMode));

    const currencyFormatter = getCurrencyFormatterSync();

    const syosMediaQuery = new SyosMediaQuery();

    const mobileFocusTrapTarget = this.$el.find('.tn-syos__layout-container').get(0) as HTMLElement;

    const iconRenderer = new IconRenderer(config);

    const isDisplayedInAModal = config.selectionMode === 'flex';

    const labels = providedLabels || buildStandardSyosLabels();

    const priceTypeSelectionHelper: IPriceTypeSelectionHelper = config.selectionMode === 'flex'
      ? new FlexPriceTypeSelectionHelper(config.priceTypeQuantities)
      : new StandardPriceTypeSelectionHelper();

    this.announce = new SyosAnnounce(this);
    this.dataService = new SyosApiDataService(this, performanceId, packageId, currencyFormatter, config);
    this.legend = new SyosLegend(this, iconRenderer, config, labels, syosMediaQuery);
    this.cartContainer = new SyosCartContainer(this, currencyFormatter, priceTypeSelectionHelper, labels);
    this.priceTypeSelector = new SyosPriceTypeSelector(this, labels, priceTypeSelectionHelper);
    this.seatInfoPopover = new SyosSeatInfoPopover(this, labels, priceTypeSelectionHelper);
    this.busyIndicator = new SyosBusyIndicator(this);
    this.mobileCartControls = new SyosMobileCartControls(this, config.rootHeadingLevel, priceTypeSelectionHelper, labels);
    this.screenContainer = new SyosScreenContainer(this, config);
    this.zoomControl = new ViewportZoomControl(config.rootHeadingLevel);
    this.mapControlsList = new SyosMapControlsList(this, this.zoomControl);
    this.seatContainer = new SyosSeatContainer(this, this.zoomControl, iconRenderer, priceTypeSelectionHelper, config.rootHeadingLevel);
    this.screenHeader = new SyosScreenHeader(this, syosMediaQuery, isDisplayedInAModal);

    if (!isDisplayedInAModal) {
      this.mobileFocusTrapper = new SyosMobileFocusTrapper(this, mobileFocusTrapTarget, syosMediaQuery);
    }

    this.on(SyosEvents.CART_UPDATE, this.onCartUpdate);
    this.on(SyosEvents.RESERVE_SEATS, this.reserveTempCartSeats);
    this.on(SyosEvents.API_REQUEST_ERROR, () => SyosAjaxErrorModal.open(this));

    this.on(SyosEvents.LEGEND_DETAILS_SHOW, () => this.onLegendShow());
    this.on(SyosEvents.LEGEND_DETAILS_HIDE, () => this.onLegendHide());
    this.on(SyosEvents.SEAT_LIST_DATA_LOADED, () => this.onSeatListLoaded());

    this.load();

    loadModal();
  }

  find(selector: string) {
    return this.$el?.find(selector);
  }

  append($el: HTMLElement | JQuery<HTMLElement>) {
    return this.$el?.append($el);
  }

  getDataService() { return this.dataService; }

  getCartContainer() { return this.cartContainer; }

  getSeatContainer() { return this.seatContainer; }

  getIsUsingCustomBirdsEye() {
    return this.screenContainer?.getIsUsingCustomBirdsEye() || false;
  }

  getMaximumSeatCount() {
    return this.maximumSeatCount;
  }

  getScreenId() {
    return this.screenId;
  }

  onCartUpdate = () => {
    const seatCount = this.cartContainer?.getSeatCount() || 0;
    this.$el?.toggleClass(StateClass.WITH_SEATS_SELECTED, seatCount !== 0);
  };

  onLegendHide() {
    this.$el?.removeClass(StateClass.LEGEND_DETAILS_VISIBLE);
  }

  onLegendShow() {
    this.$el?.addClass(StateClass.LEGEND_DETAILS_VISIBLE);
  }

  async load() {
    const initDataResponse = await this.dataService.getInitData();

    if (!initDataResponse) {
      this.onInitializationError();

      return;
    }

    this.maximumSeatCount = initDataResponse.maximumSeatCount;

    this.screenContainer?.setData(initDataResponse.screensData);
  }

  protected onInitializationError() {
    window.location.href = `${this.config.bestAvailableBasePath}?z=0`;
  }

  getCurrentScreen() { return this.currentScreen; }

  setCurrentScreen(screen?: Screen) {
    if (screen === this.currentScreen) {
      return;
    }

    this.currentScreen = screen;

    if (!screen) {
      toggleBodyClass(StateClass.VIEWING_SEATS, false);
    }

    this.emit(SyosEvents.SCREEN_UPDATED, { screen });
  }

  onSeatListLoaded = () => {
    toggleBodyClass(StateClass.VIEWING_SEATS, true);
  };

  unsetCurrentScreen() {
    this.setCurrentScreen(undefined);
  }

  hideSeatContainer() {
    this.seatContainer?.hide();
  }

  setFullScreenIsEnabled() {
    this.$el?.addClass(StateClass.FULL_SCREEN_ENABLED);
  }

  reserveTempCartSeats = async () => {
    if (!this.cartContainer) {
      return;
    }

    const seats = this.cartContainer.getSeats();

    if (seats.size === 0) {
      return;
    }

    this.emit(SyosEvents.API_RESERVION_ATTEMPT);

    const data = await this.makeReservationRequest([...seats.values()]);

    if (data.isSuccessful) {
      this.emit(SyosEvents.API_RESERVATION_SUCCESS);

      data.onSuccess?.();

      return;
    }

    this.emit(SyosEvents.API_RESERVATION_FAIL);

    let { errorMessage } = data;

    if (this.currentScreen) {
      errorMessage += '<p>Seat availability is being refreshed.</p>';
    }

    if (errorMessage) {
      const errorTitle = data.errorTitle || SyosPageComponent.reservationFailureModalTitle;

      loadModal().then((Modal) => {
        Modal.alert({
          title: errorTitle,
          bodyContents: errorMessage,
          describedById: 'tn-reservation-failed-seats-availability-refresh-message',
        });
      });

      this.cartContainer.empty();

      if (this.currentScreen) {
        this.seatContainer?.load(this.currentScreen.id);
      }
    }
  };

  redirectToBestAvailable(zoneId: number) {
    document.location.href = `${this.config.bestAvailableBasePath}?z=${zoneId}`;
  }

  protected async makeReservationRequest(seats: SyosSeat[]): Promise<ISyosSeatReservationResult> {
    const data = await this.dataService.reserveTempCartSeats(seats);

    const numberRequested = data.num_seats_requested;
    const numberReserved = data.num_seats_reserved;

    const isSuccessful = numberRequested === numberReserved;

    if (isSuccessful) {
      return {
        isSuccessful: true,
        onSuccess: () => {
          window.location = data.RedirectUrl;
        },
      };
    }

    let errorMessage: string;

    if (data.HasError && data.ErrorMessage) {
      errorMessage = `<p>${data.ErrorMessage}</p>`;
    } else if (numberReserved !== numberRequested) {
      errorMessage = '<p>The seats that you requested could not be reserved.</p>';
    } else {
      errorMessage = '<p>An unknown error has occurred</p>';
    }

    return {
      isSuccessful: false,
      errorMessage,
      errorTitle: data.ErrorTitle,
    };
  }

  destroy() {
    this.$el?.off();
    this.$el = undefined;

    this.removeAllListeners();

    this.unsetCurrentScreen();

    this.announce = undefined;

    this.mapControlsList?.destroy();
    this.mapControlsList = undefined;

    this.screenContainer?.destroy();
    this.screenContainer = undefined;

    this.mobileCartControls?.destroy();
    this.mobileCartControls = undefined;

    this.busyIndicator?.destroy();
    this.busyIndicator = undefined;

    this.seatInfoPopover?.destroy();
    this.seatInfoPopover = undefined;

    this.priceTypeSelector?.destroy();
    this.priceTypeSelector = undefined;

    this.seatContainer?.destroy();
    this.seatContainer = undefined;

    this.cartContainer?.destroy();
    this.cartContainer = undefined;

    this.legend?.destroy();
    this.legend = undefined;

    this.screenHeader?.destroy();
    this.screenHeader = undefined;

    this.mobileFocusTrapper?.destroy();
    this.mobileFocusTrapper = undefined;
    
    this.zoomControl?.destroy();
    this.zoomControl = undefined;
  }
}

export default SyosController;
